/**
 * Top-of-app banner shown on non-prod environments.
 *
 * Driven by VITE_DEPLOY_ENV at build time:
 *   - "production" or unset → no banner (prod = invisible chrome)
 *   - "staging"             → amber banner, "STAGING — not production"
 *   - any other value       → amber banner with that value as the label
 *
 * The Vercel staging project sets VITE_DEPLOY_ENV=staging in its env
 * vars; Vercel prod doesn't set it (or sets it to "production").
 *
 * Why a banner instead of a different favicon / different brand color:
 * the URLs are nearly identical and humans rapid-switch between tabs.
 * A bright sticky bar at the top is the loudest signal we can give
 * without being distracting in the actual page chrome below.
 */
export function StagingBanner() {
  const env = (import.meta.env.VITE_DEPLOY_ENV as string | undefined)?.toLowerCase();
  if (!env || env === "production" || env === "prod") return null;

  const label = env === "staging" ? "STAGING" : env.toUpperCase();
  const subtext =
    env === "staging"
      ? "Not production — data here is disposable"
      : "Non-production environment";

  return (
    <div className="sticky top-0 z-50 flex items-center justify-center gap-3 border-b border-amber-300 dark:border-amber-500/40 bg-amber-100 dark:bg-amber-500/15 px-4 py-1.5 text-[12px] font-medium text-amber-900 dark:text-amber-300 shadow-sm">
      <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-amber-500" />
      <span className="font-bold tracking-wider">{label}</span>
      <span className="text-amber-700 dark:text-amber-300">·</span>
      <span className="text-amber-800 dark:text-amber-300">{subtext}</span>
    </div>
  );
}
